import { nextTick, onMounted, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { buildRequestErrorMessage, formatFileSize } from './shared';

const getAbVinFromName = (name) => {
  if (!name) return '';
  const lower = name.toLowerCase();
  if (!lower.endsWith('.ab')) return '';
  return name.slice(0, -3);
};

export function useMazdaAb(mazdaPost, serverName) {
  const abFile = ref(null);
  const abVin = ref('');
  const abUploadResult = ref(null);
  const abItems = ref([]);
  const abTotal = ref(0);
  const abPage = ref(1);
  const abPageSize = ref(10);
  const abKeyword = ref('');
  const abJumpPage = ref('1');
  const abUploading = ref(false);
  const abListLoading = ref(false);
  const abContentDialogVisible = ref(false);
  const abDetailRow = ref({});
  const abDetailContent = ref('');
  const abDetailLoading = ref(false);
  const abUploadRef = ref(null);

  const handleAbFileChange = (file) => {
    abFile.value = file.raw;
    abVin.value = getAbVinFromName(file?.raw?.name || file?.name || '');
  };

  const clearAbFile = async () => {
    abFile.value = null;
    abVin.value = '';
    await nextTick();
    if (abUploadRef.value) {
      abUploadRef.value.clearFiles();
    }
  };

  const fetchAbList = async () => {
    abListLoading.value = true;
    try {
      const response = await mazdaPost('listAbData', {
        page: abPage.value,
        pageSize: abPageSize.value,
        keyword: abKeyword.value,
      });

      const data = response.data || {};
      if (data.success) {
        abItems.value = (Array.isArray(data.items) ? data.items : [])
          .slice()
          .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
        abTotal.value = Number(data.total || 0);
        abPage.value = Number(data.page || abPage.value);
        abPageSize.value = Number(data.pageSize || abPageSize.value);
        abJumpPage.value = String(abPage.value);
      } else {
        abItems.value = [];
        abTotal.value = 0;
        ElMessage.error(data.message || 'AB 数据查询失败');
      }
    } catch (error) {
      abItems.value = [];
      abTotal.value = 0;
      ElMessage.error(buildRequestErrorMessage('AB 数据查询失败', error));
    } finally {
      abListLoading.value = false;
    }
  };

  const refreshAbList = async () => {
    abJumpPage.value = String(abPage.value);
    await fetchAbList();
  };

  const submitAbUpload = async () => {
    if (!abFile.value) {
      ElMessage.warning('请先选择 .ab 文件');
      return;
    }
    if (!String(abFile.value.name || '').toLowerCase().endsWith('.ab')) {
      ElMessage.error('只能上传 .ab 文件');
      return;
    }
    if (!abVin.value) {
      ElMessage.error('无法从文件名识别 VIN');
      return;
    }

    abUploading.value = true;
    try {
      const formData = new FormData();
      formData.append('file', abFile.value);

      const response = await mazdaPost('uploadAbData', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      const data = response.data || {};
      abUploadResult.value = data;

      if (data.success) {
        ElMessage.success(data.message || 'AB 数据上传成功');
        await clearAbFile();
        await refreshAbList();
      } else {
        ElMessage.error(data.message || 'AB 数据上传失败');
      }
    } catch (error) {
      if (error?.response) {
        abUploadResult.value = error.response.data || null;
      }
      ElMessage.error(buildRequestErrorMessage('AB 数据上传失败', error));
    } finally {
      abUploading.value = false;
    }
  };

  const handleAbSearch = async () => {
    abPage.value = 1;
    await fetchAbList();
  };

  const handleAbPageChange = async (page) => {
    abPage.value = page;
    abJumpPage.value = String(page);
    await fetchAbList();
  };

  const handleAbSizeChange = async (size) => {
    abPageSize.value = Number(size);
    abPage.value = 1;
    abJumpPage.value = '1';
    await fetchAbList();
  };

  const handleAbJump = async () => {
    const page = Number(abJumpPage.value);
    const maxPage = Math.max(1, Math.ceil((abTotal.value || 0) / (abPageSize.value || 10)));
    if (!Number.isFinite(page) || page < 1) {
      abJumpPage.value = String(abPage.value);
      return;
    }
    abPage.value = Math.min(page, maxPage);
    abJumpPage.value = String(abPage.value);
    await fetchAbList();
  };

  const showAbContent = async (row) => {
    abDetailRow.value = row || {};
    abDetailContent.value = '';
    abContentDialogVisible.value = true;

    const id = Number(row?.id || 0);
    if (!Number.isFinite(id) || id <= 0) {
      abDetailContent.value = row?.contentPreview || '';
      ElMessage.error('AB 数据详情 id 无效');
      return;
    }

    abDetailLoading.value = true;
    try {
      const response = await mazdaPost('getAbDataDetail', { id });
      const data = response.data || {};
      if (data.success) {
        abDetailRow.value = {
          ...(row || {}),
          id: data.id ?? row?.id,
          vin: data.vin ?? row?.vin,
          fileName: data.fileName ?? row?.fileName,
          xmlLength: data.xmlLength ?? row?.xmlLength,
        };
        abDetailContent.value = data.content || '';
      } else {
        abDetailContent.value = row?.contentPreview || '';
        ElMessage.error(data.message || 'AB 数据详情读取失败');
      }
    } catch (error) {
      abDetailContent.value = row?.contentPreview || '';
      ElMessage.error(buildRequestErrorMessage('AB 数据详情读取失败', error));
    } finally {
      abDetailLoading.value = false;
    }
  };

  onMounted(refreshAbList);
  watch(serverName, refreshAbList);

  return {
    abFile,
    abVin,
    abUploadResult,
    abItems,
    abTotal,
    abPage,
    abPageSize,
    abKeyword,
    abJumpPage,
    abUploading,
    abListLoading,
    abContentDialogVisible,
    abDetailRow,
    abDetailContent,
    abDetailLoading,
    abUploadRef,
    formatFileSize,
    handleAbFileChange,
    clearAbFile,
    submitAbUpload,
    refreshAbList,
    handleAbSearch,
    handleAbPageChange,
    handleAbSizeChange,
    handleAbJump,
    showAbContent,
  };
}
